import { Client } from '../common';
import { apiConfig } from '../../config';
import { BEAM_ERROR_CODES } from '../../common/utils/constants';

const STATUS_CHECK_INTERVAL_MS = 2000;

export class BeamError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

export function getBeamKPIs({ deviceId, beamParams, acquisitionInfo }) {
  const url = apiConfig.cmServiceGetBeamKPIs(deviceId);
  
  const auth = window.__APP_AUTH;
  const headers = {
    Accept: '*/*',
    'Content-Type': 'application/json',
    'cache-control': 'no-cache',
    Authorization: 'Bearer ' + auth?.accessToken,
    'accept-encoding': 'gzip, deflate'
  };

  const body = JSON.stringify({
    azimuth: beamParams.azimuth,
    elevation: beamParams.elevation,
    polarity: acquisitionInfo.polarization === 'horizontal' ? 'hpol' : 'vpol',
    pci: acquisitionInfo.pci,
    'ssb-arfcn': acquisitionInfo['ssb-arfcn'],
    scs: acquisitionInfo.scs
  });

  const requestOptions = {
    method: 'POST',
    headers: headers,
    body: body,
    redirect: 'follow'
  };

  return new Promise((resolve, reject) => {
    fetch(url, requestOptions)
      .then((res) => {
        // Check for 202 status code (Accepted)
        if (res.status === 202) {
          res.json().then((data) => {
            checkBeamStatus(data.statusQueryGetUri, null, resolve, reject);
          });
          return;
        }
        
        // For other successful status codes, parse the response
        if (res.ok) {
          res.json().then((data) => {
            // Parse and validate 5G KPI metrics from response
            const kpiMetrics = {
              power: data.power,
              rssi: data.rssi,
              pci: data['5g-kpis']?.[0]?.pci,
              issb: data['5g-kpis']?.[0]?.issb,
              ssRsrp: data['5g-kpis']?.[0]?.['ss-rsrp'],
              ssRsrq: data['5g-kpis']?.[0]?.['ss-rsrq'],
              ssSinr: data['5g-kpis']?.[0]?.['ss-sinr'],
              cellBarred: data['5g-kpis']?.[0]?.['cell-barred']
            };
            resolve(kpiMetrics);
          }).catch(error => reject(error));
          return;
        }
        
        // Handle error responses
        res.text().then((data) => {
          reject(data);
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function mapErrorCodeFromResponse(response) {
  const errorMessage = response.data?.message?.toLowerCase() || '';
  
  if (errorMessage.includes('sector')) return BEAM_ERROR_CODES.SECTOR_OP_ENABLED;
  if (errorMessage.includes('polarity')) return BEAM_ERROR_CODES.INVALID_POLARITY;
  if (errorMessage.includes('pci')) return BEAM_ERROR_CODES.INVALID_PCI;
  if (errorMessage.includes('ssb')) return BEAM_ERROR_CODES.INVALID_SSB_ARFCN;
  if (errorMessage.includes('scs')) return BEAM_ERROR_CODES.INVALID_SCS;
  
  return 'unknown_error';
}

/**
 * periodically call query URI to check status until execution is completed
 * @param {string} uri
 * @param {object} data
 * @param {Function} resolve
 * @param {Function} reject
 */
const checkBeamStatus = (uri, data, resolve, reject) => {
  const auth = window.__APP_AUTH;
  const headers = {
    Accept: '*/*',
    'Content-Type': 'application/json',
    'cache-control': 'no-cache',
    Authorization: 'Bearer ' + auth?.accessToken,
    'accept-encoding': 'gzip, deflate'
  };

  const requestOptions = {
    method: 'GET',
    headers: headers,
    redirect: 'follow'
  };

  if (data == null) {
    fetch(uri, requestOptions)
      .then((res) => res.json())
      .then((data) => {
        checkBeamStatus(uri, data, resolve, reject);
      })
      .catch((error) => {
        reject(error);
      });
    return;
  }
  if (data.runtimeStatus !== 'Pending' && data.runtimeStatus !== 'Running') {
    // Check if there's an error in the response
    if (data.runtimeStatus === 'Failed') {
      const errorCode = mapErrorCodeFromResponse(data);
      reject(new BeamError(errorCode, data.output?.message || 'Failed to get beam KPIs'));
      return;
    }
    
    // Parse the KPI metrics from the response
    const responseData = data.output?.status_info?.result?.payload;
    if (responseData) {
      const kpiMetrics = {
        power: responseData.power,
        rssi: responseData['5g-kpis']?.[0]?.rssi,
        pci: responseData['5g-kpis']?.[0]?.pci,
        issb: responseData['5g-kpis']?.[0]?.issb,
        ssRsrp: responseData['5g-kpis']?.[0]?.['ss-rsrp'],
        ssRsrq: responseData['5g-kpis']?.[0]?.['ss-rsrq'],
        ssSinr: responseData['5g-kpis']?.[0]?.['ss-sinr'],
        cellBarred: responseData['5g-kpis']?.[0]?.['cell-barred']
      };
      resolve(kpiMetrics);
    } else {
      resolve(data);
    }
    return;
  }

  setTimeout(() => {
    fetch(uri, requestOptions)
      .then((res) => res.json())
      .then((data) => {
        checkBeamStatus(uri, data, resolve, reject);
      })
      .catch((error) => reject(error));
  }, STATUS_CHECK_INTERVAL_MS);
};
